/**
 * Agent Registration Page
 * 부동산 에이전트 등록 페이지 - ABN 검증 및 플랜 선택
 */

import { useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Building2, Shield, CheckCircle2, AlertCircle } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Badge } from "@/components/ui/badge";

// 에이전트 플랜 목록
const agentPlans = [
  {
    value: "basic",
    label: "Agent Basic",
    price: "$49",
    period: "월",
    features: ["매물 10개/월", "기본 노출"],
  },
  {
    value: "plus",
    label: "Agent Plus",
    price: "$99",
    period: "월",
    features: ["매물 30개/월", "상단 Featured 노출 1개"],
  },
  {
    value: "premium",
    label: "Agent Premium",
    price: "$199",
    period: "월",
    features: ["무제한 등록", "OZK 메인 추천", "로고 노출 강화"],
  },
];

export default function AgentRegistration() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [abn, setAbn] = useState<string>("");
  const [reaLicence, setReaLicence] = useState<string>("");
  const [selectedPlan, setSelectedPlan] = useState<"basic" | "plus" | "premium">("basic");
  const [isVerifying, setIsVerifying] = useState(false);
  const [abnVerified, setAbnVerified] = useState(false);
  const [abnData, setAbnData] = useState<any>(null);

  const verifyMutation = useMutation({
    mutationFn: async () => {
      if (!abn) throw new Error("ABN을 입력해주세요");

      const token = await user?.getIdToken();
      const res = await fetch("/api/trust/verify-abn", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ abn }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "ABN 검증 실패");
      }

      const data = await res.json();
      return data;
    },
    onSuccess: (data) => {
      setAbnVerified(true);
      setAbnData(data);
      toast({
        title: "ABN 검증 완료",
        description: `${data.entityName || "사업자"} 인증이 완료되었습니다.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "ABN 검증 실패",
        description: error.message,
        variant: "destructive",
      });
      setAbnVerified(false);
    },
  });

  const registerMutation = useMutation({
    mutationFn: async () => {
      if (!abnVerified) throw new Error("ABN 검증을 먼저 완료해주세요");
      if (!user) throw new Error("로그인이 필요합니다");

      const token = await user.getIdToken();
      const res = await fetch("/api/properties/agent-register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          abn,
          rea_licence: reaLicence || undefined,
          plan: selectedPlan,
          user_id: user.uid,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "에이전트 등록 실패");
      }

      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "에이전트 등록 완료",
        description: "에이전트로 등록되었습니다. 이제 매물을 등록할 수 있습니다.",
      });
      setLocation("/properties/new");
    },
    onError: (error: Error) => {
      toast({
        title: "등록 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleVerifyABN = () => {
    setIsVerifying(true);
    verifyMutation.mutate();
    setTimeout(() => setIsVerifying(false), 2000);
  };

  return (
    <PageContainer size="default">
      <Button
        variant="ghost"
        onClick={() => setLocation("/properties")}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        목록으로
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            부동산 에이전트 등록
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 안내 메시지 */}
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-semibold mb-1">에이전트 등록 안내</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>ABN 기반 사업자 인증이 필요합니다</li>
                  <li>REA Licence는 선택 사항입니다</li>
                  <li>에이전트로 등록하면 무제한 매물 등록이 가능합니다</li>
                  <li>플랜에 따라 월 등록 한도가 다릅니다</li>
                </ul>
              </div>
            </div>
          </div>

          {/* ABN 입력 */}
          <div className="space-y-2">
            <Label htmlFor="abn">ABN (Australian Business Number) *</Label>
            <div className="flex gap-2">
              <Input
                id="abn"
                placeholder="11자리 ABN 번호"
                value={abn}
                onChange={(e) => {
                  setAbn(e.target.value.replace(/\D/g, "").slice(0, 11));
                  setAbnVerified(false);
                }}
                maxLength={11}
              />
              <Button
                type="button"
                onClick={handleVerifyABN}
                disabled={!abn || abn.length !== 11 || isVerifying || verifyMutation.isPending}
              >
                {isVerifying || verifyMutation.isPending ? "검증 중..." : "검증"}
              </Button>
            </div>
            {abnVerified && abnData && (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="w-4 h-4" />
                <span>
                  {abnData.entityName || "사업자"} 인증 완료 (ABN: {abnData.abn})
                </span>
              </div>
            )}
            {verifyMutation.isError && (
              <div className="text-sm text-red-600 dark:text-red-400">
                {verifyMutation.error?.message || "ABN 검증 실패"}
              </div>
            )}
          </div>

          {/* REA Licence (선택) */}
          <div className="space-y-2">
            <Label htmlFor="rea_licence">
              REA Licence Number (선택)
            </Label>
            <Input
              id="rea_licence"
              placeholder="Real Estate Agent Licence Number"
              value={reaLicence}
              onChange={(e) => setReaLicence(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Real Estate Agent Licence Number가 있으시면 입력해주세요
            </p>
          </div>

          {/* 플랜 선택 */}
          <div className="space-y-4">
            <Label>에이전트 플랜 선택</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {agentPlans.map((plan) => (
                <Card
                  key={plan.value}
                  className={`cursor-pointer transition-all ${
                    selectedPlan === plan.value
                      ? "border-primary border-2"
                      : "border"
                  }`}
                  onClick={() => setSelectedPlan(plan.value as any)}
                >
                  <CardHeader>
                    <CardTitle className="text-lg">{plan.label}</CardTitle>
                    <div className="text-2xl font-bold text-primary">
                      {plan.price}
                      <span className="text-sm font-normal text-muted-foreground">
                        /{plan.period}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* 등록 버튼 */}
          <div className="flex gap-4">
            <Button
              onClick={() => registerMutation.mutate()}
              disabled={!abnVerified || registerMutation.isPending}
              className="flex-1"
              size="lg"
            >
              {registerMutation.isPending ? "등록 중..." : "에이전트로 등록하기"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setLocation("/properties")}
            >
              취소
            </Button>
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  );
}

